import { useMemo, useState } from 'react';

// ADR-0020: a kind the user makes up is OTHER plus a name. The names already
// used in the trip come back as options, so a made-up kind can be reused
// without being a new enum value (icon, colour, AI and schedule stay intact).

const NEW = '__new__';
const CUSTOM = 'custom:';

interface Props<T extends string> {
  options: { value: T; label: string }[];
  otherValue: T;
  value: T;
  customName: string;
  existingNames: string[];
  onChange: (value: T, customName: string) => void;
  labels: { yours: string; create: string; name: string; namePlaceholder: string };
  ariaLabel?: string;
}

export default function KindSelect<T extends string>({
  options, otherValue, value, customName, existingNames, onChange, labels, ariaLabel,
}: Props<T>) {
  const names = useMemo(
    () => [...new Set(existingNames.map(n => n.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [existingNames],
  );
  // Tracked apart from the name: typing a new one that happens to match an
  // existing kind must not swap the input for the option mid-word.
  const [creating, setCreating] = useState(value === otherValue && !names.includes(customName.trim()));

  const selected = value !== otherValue ? value : creating ? NEW : `${CUSTOM}${customName.trim()}`;

  function handleSelect(next: string) {
    if (next === NEW) { setCreating(true); onChange(otherValue, ''); return; }
    setCreating(false);
    if (next.startsWith(CUSTOM)) onChange(otherValue, next.slice(CUSTOM.length));
    else onChange(next as T, '');
  }

  return (
    <>
      <select className="input" aria-label={ariaLabel} value={selected} onChange={e => handleSelect(e.target.value)}>
        {options.filter(o => o.value !== otherValue).map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        {names.length > 0 && (
          <optgroup label={labels.yours}>
            {names.map(n => <option key={n} value={`${CUSTOM}${n}`}>{n}</option>)}
          </optgroup>
        )}
        <option value={NEW}>{labels.create}</option>
      </select>
      {creating && (
        <input
          type="text" className="input mt-2" maxLength={40} autoFocus
          aria-label={labels.name} placeholder={labels.namePlaceholder}
          value={customName} onChange={e => onChange(otherValue, e.target.value)}
        />
      )}
    </>
  );
}
